import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { EnquiryDialog } from "@/pages/PublicCatalogue";
import { MessageCircle, ArrowLeft, Sparkles, Clock, GraduationCap, PhoneCall } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "917601016337";
const CONTACT_PHONE = "+91 76010 16337";
const CONTACT_EMAILS = ["miroconsultancy.in@gmail.com", "support@miroconsultancy.co.in"];

const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
function feeString(p) {
  const prefix = p?.starts_from ? "Starts from " : "";
  const suffix = p?.gst_applicable ? " + applicable GST" : "";
  return `${prefix}${inr(p?.fee)}${suffix}`;
}
const DELIVERY_LABEL = {
  online: "Online", offline: "Offline", hybrid: "Hybrid",
  blended: "Blended", online_or_blended: "Online / Blended",
};

export default function PublicCatalogueDetail() {
  const { programId } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [enquireOpen, setEnquireOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/catalogue/${programId}`)
      .then(r => setP(r.data))
      .catch(() => setNotFound(true));
  }, [programId]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[var(--miro-navy,#041B46)] text-white flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-white/50">404</div>
          <h1 className="font-display font-black text-3xl mt-3">Program not published</h1>
          <p className="text-white/60 mt-2">This course is not currently available. Please check the catalogue for our latest programs.</p>
          <Link to="/catalogue" className="inline-block mt-6 px-4 py-2 rounded-md bg-white text-[var(--miro-navy,#041B46)] font-semibold text-sm">Back to catalogue</Link>
        </div>
      </div>
    );
  }
  if (!p) return <div className="min-h-screen bg-[var(--miro-navy,#041B46)] text-white p-10">Loading…</div>;

  const waMsg = encodeURIComponent(`Hi MIRO Academy, I'm interested in "${p.title}". Please share the latest batches, fees and delivery options.`);

  return (
    <div className="min-h-screen bg-[var(--miro-navy,#041B46)] text-white">
      <Toaster position="top-right" richColors closeButton />
      {/* Header */}
      <header className="border-b border-white/10 bg-[#031132]/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/catalogue" className="flex items-center gap-2 text-sm text-white/70 hover:text-white">
            <ArrowLeft size={16}/> Catalogue
          </Link>
          <img src="/miro-logo.png" alt="MIRO" className="h-9 w-auto"/>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-14 pb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">{p.program_code || "MIRO Academy"}</div>
        <h1 className="mt-3 font-display font-black text-3xl sm:text-5xl leading-[1.05]">{p.title}</h1>
        {p.tagline && <p className="text-white/70 mt-3 text-lg italic">{p.tagline}</p>}
        {p.outcome && <p className="text-white/80 mt-4">{p.outcome}</p>}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat icon={GraduationCap} label="Fee" value={feeString(p)} accent="cyan"/>
          <Stat icon={Clock} label="Duration"
                value={p.duration_verified ? `${p.duration_value} ${p.duration_unit}` : "Editable per batch"}
                accent="emerald"/>
          <Stat icon={PhoneCall} label="Delivery mode" value={DELIVERY_LABEL[p.delivery_mode] || p.delivery_mode} accent="amber"/>
        </div>

        {p.emi_available && (
          <div className="mt-3 text-sm text-emerald-300">EMI options available</div>
        )}
        {p.admission_fee_applicable && (
          <div className="mt-1 text-xs text-white/60">
            One-time admission fee {inr(p.admission_fee || 2000)} applies separately.
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <Button data-testid="detail-enquire-btn" onClick={() => setEnquireOpen(true)}
                  className="bg-white text-[var(--miro-navy,#041B46)] hover:bg-white/90 h-11 px-6">
            {p.cta_label || "Enquire Now"}
          </Button>
          <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`} target="_blank" rel="noreferrer"
             data-testid="detail-wa-btn"
             className="inline-flex items-center gap-2 rounded-md h-11 px-5 text-sm font-semibold border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10">
            <MessageCircle size={16}/> WhatsApp
          </a>
          <Link to="/leadform" data-testid="detail-counselling-btn"
                className="inline-flex items-center gap-2 rounded-md h-11 px-5 text-sm font-semibold border border-white/15 text-white/80 hover:bg-white/5">
            Book a Counselling Session
          </Link>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-5xl mx-auto px-6 pb-16 space-y-10">
        {p.description && (
          <Block title="Course overview" body={p.description}/>
        )}
        {p.who_its_for && (
          <Block title="Who it is for" body={p.who_its_for}/>
        )}
        {p.curriculum_outline && (
          <Block title="Curriculum & modules" body={p.curriculum_outline}/>
        )}

        {p.faqs?.length > 0 && (
          <div>
            <h2 className="font-display font-black text-2xl mb-4">Frequently asked questions</h2>
            <div className="space-y-2">
              {p.faqs.map((f, i) => (
                <details key={f.q || `faq-${i}`} className="rounded-lg border border-white/10 bg-white/5 p-4 group">
                  <summary className="cursor-pointer font-semibold text-white">
                    {f.q}
                  </summary>
                  <div className="text-white/70 text-sm mt-2">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        )}

        {p.disclaimer && (
          <div className="text-xs leading-relaxed text-white/60 rounded-xl border border-amber-400/25 bg-amber-500/5 p-4">
            <div className="font-semibold text-amber-200 uppercase tracking-wider text-[11px] mb-1">Important</div>
            {p.disclaimer}
          </div>
        )}

        <div className="text-xs leading-relaxed text-white/60 rounded-xl border border-white/10 bg-white/5 p-4">
          Course fees shown are indicative starting prices and may vary based on batch, level, delivery mode, partner, learning materials, examination fees and promotional offers. Contact MIRO Academy for the latest approved fee and batch details.
        </div>
      </section>

      {enquireOpen && <EnquiryDialog program={p} onClose={() => setEnquireOpen(false)}/>}

      {/* Footer contact */}
      <footer className="border-t border-white/10 bg-[#03102b]/80">
        <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <img src="/miro-logo.png" alt="MIRO" className="h-8 w-auto mb-3"/>
            <div className="text-white/60">
              MIRO Consultancy — Academy, Study Abroad, Wealth &amp; Digital services.
            </div>
          </div>
          <div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-white/50 mb-2">Talk to us</div>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi MIRO Academy, I'd like more information about ${p.title}.`)}`}
               target="_blank" rel="noreferrer"
               data-testid="detail-wa-footer"
               className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 transition-colors">
              <MessageCircle size={14}/> WhatsApp {CONTACT_PHONE}
            </a>
            <div className="text-white/60 mt-2">
              <a href={`tel:${WHATSAPP_NUMBER}`} className="hover:text-white">Call {CONTACT_PHONE}</a>
            </div>
          </div>
          <div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-white/50 mb-2">Email</div>
            <div className="flex flex-col gap-1">
              {CONTACT_EMAILS.map(em => (
                <a key={em} href={`mailto:${em}`} className="text-white/70 hover:text-white truncate">
                  {em}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6 py-3 text-[11px] text-white/40 flex flex-wrap justify-between gap-2">
            <div>© {new Date().getFullYear()} MIRO Consultancy. All rights reserved.</div>
            <div>Prices and batches may change. Contact MIRO Academy for the latest details.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const map = {
    cyan: "bg-cyan-500/15 text-cyan-300",
    emerald: "bg-emerald-500/15 text-emerald-300",
    amber: "bg-amber-500/15 text-amber-300",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${map[accent]}`}>
        <Icon size={17}/>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-white/50">{label}</div>
        <div className="font-display font-bold text-lg leading-tight">{value}</div>
      </div>
    </div>
  );
}

function Block({ title, body }) {
  return (
    <div>
      <h2 className="font-display font-black text-2xl mb-3">{title}</h2>
      <div className="text-white/75 leading-relaxed whitespace-pre-line">{body}</div>
    </div>
  );
}
